/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { coerceElement } from '@angular/cdk/coercion';
import { ElementRef, inject, Injectable } from '@angular/core';
import { NgfForm } from '@ng-falcon/ngf-form/+model/ngf-form';
import { NgfControlError } from '@ng-falcon/ngf-form/ngf-control/ngf-control-error/ngf-control-error';
import { asyncScheduler, BehaviorSubject } from 'rxjs';
import { map, observeOn } from 'rxjs/operators';

@Injectable()
export abstract class NgfFormService {
  abstract readonly $ref: NgfForm<any>;

  readonly #elementRef: ElementRef<HTMLElement>;

  private readonly errors = new BehaviorSubject<readonly NgfControlError[]>([]);
  readonly errors$ = this.errors.pipe(observeOn(asyncScheduler));
  readonly hasErrors$ = this.errors$.pipe(map(errors => errors.length > 0));

  constructor() {
    this.#elementRef = inject(ElementRef);
  }

  getErrors(): readonly NgfControlError[] {
    return this.errors.value;
  }

  scrollToFirstError(): void {
    // we have to call it in setTimeout
    // to wait for any new errors to be initialized
    setTimeout(() => {
      if (this.errors.value.length === 0) {
        return;
      }
      this.scrollToError(this.errors.value[0]);
    }, 100);

  }

  scrollToError(ngfControlError: NgfControlError): void {
    const container = ngfControlError.container.nativeElement;
    container.scrollIntoView({ behavior: 'smooth', block: 'end' });

    // we try to focus first focusable element within container
    // first we are looking for invalid focusable element
    // if there are no invalid elements (for example when all FormControls are valid, but FormGroup is invalid)
    // then we are looking for any focusable element within container
    const invalidElement = container.querySelector<HTMLElement>('.ng-invalid');
    const focusableElement = invalidElement
      ? this.findFocusableElementWithin(invalidElement)
      : this.findFocusableElementWithin(container);

    focusableElement?.focus({ preventScroll: true });
  }

  addError(error: NgfControlError): void {
    const newErrors = [
      ...this.errors.value,
      error
    ];

    this.sortErrorsByOrderInHtml(newErrors);
    this.errors.next(newErrors);
  }

  removeError(error: NgfControlError): void {
    const newErrors = this.errors.value.filter(it => it !== error);
    this.errors.next(newErrors);
  }

  private sortErrorsByOrderInHtml(errors: NgfControlError[]): void {
    let tags: string[] = [];

    errors.forEach(error => {
      const tag = error.element.nativeElement.tagName;
      if (!tags.includes(tag)) {
        tags.push(tag);
      }
    });

    const order = Array.from(this.#elementRef.nativeElement.querySelectorAll<HTMLElement>(tags.join(',')));
    errors.sort((error1, error2) => order.indexOf(error1.element.nativeElement) - order.indexOf(error2.element.nativeElement));
  }

  private findFocusableElementWithin(root: HTMLElement | ElementRef<HTMLElement>): HTMLElement | null {
    const element = coerceElement(root);

    if (element instanceof HTMLInputElement) {
      return !element.disabled ? element : null;
    } else if (element instanceof HTMLSelectElement) {
      return !element.disabled ? element : null;
    } else if (element instanceof HTMLTextAreaElement) {
      return !element.disabled ? element : null;
    } else if (element.tabIndex >= 0) {
      return element;
    }

    return element.querySelector(`
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      [tabindex]:not([tabindex="-1"])
    `);
  }
}
